import React, { useImperativeHandle, useRef } from "react";
import type { FormFieldRef } from "../../../../components/forms/types";
import type { FormFieldHandle } from "../../../../components/forms/types";
import type { SectionValidationResult } from "../../../../lib/forms/validation";
import type { FieldDto } from "../../../../types/field";
import type { SectionDto } from "../../../../types/section";
import { SectionFields } from "./SectionFields";

export type { SectionValidationResult };

interface SectionComponentProps {
  section: SectionDto;
  visibleByName: Record<string, boolean>;
  valuesByName?: Record<string, string>;
  onFieldValueChange: (fieldName: string, value: string) => void;
  introText?: string;
  showLabel?: boolean;
}

export type SectionComponentRefs = {
  refs: React.MutableRefObject<Map<string, FormFieldRef>>;
  validate: (visibleByName: Record<string, boolean>) => SectionValidationResult;
  clear: () => void;
} | null;

const SectionComponent = React.forwardRef<
  SectionComponentRefs,
  SectionComponentProps
>(
  (
    {
      section,
      visibleByName,
      valuesByName = {},
      onFieldValueChange,
      introText,
      showLabel = true,
    },
    ref,
  ) => {
    const inputsRefs = useRef<Map<string, FormFieldRef>>(new Map());

    const sortedFields = [...(section.fields ?? [])].sort(
      (a, b) => a.order - b.order,
    );

    const validateInputs = (
      visibility: Record<string, boolean>,
    ): SectionValidationResult => {
      let valid = true;
      let firstInvalidField: FormFieldHandle | null = null;
      let invalidCount = 0;

      sortedFields.forEach((field) => {
        if (visibility[field.name] === false) return;

        const input = inputsRefs.current.get(field.name);
        if (!input) return;

        if (!input.validate()) {
          valid = false;
          invalidCount += 1;
          if (!firstInvalidField) firstInvalidField = input;
        }
      });

      return { valid, firstInvalidField, invalidCount };
    };

    const clear = () => {
      inputsRefs.current.forEach((input) => input?.clear());
    };

    useImperativeHandle(ref, () => ({
      refs: inputsRefs,
      validate: validateInputs,
      clear,
    }));

    const registerRef = (field: FieldDto) => (instance: FormFieldRef) => {
      if (instance) {
        inputsRefs.current.set(field.name, instance);
      } else {
        inputsRefs.current.delete(field.name);
      }
    };

    return (
      <section className="pt-1.5 first:pt-0">
        {showLabel && section.label.trim() ? (
          <h2 className="form-section__heading mb-3 font-onest text-base font-semibold text-ink">
            {section.label}
          </h2>
        ) : null}
        <SectionFields
          section={section}
          visibleByName={visibleByName}
          valuesByName={valuesByName}
          onFieldValueChange={onFieldValueChange}
          registerRef={registerRef}
          introText={introText}
        />
      </section>
    );
  },
);

SectionComponent.displayName = "SectionComponent";

export default SectionComponent;
